import type { Route } from './+types/services';
import { Link } from 'react-router';
import { RelatedPages } from '~/components/related-pages';
import { ResponsiveImage } from '~/components/ui/responsive-image';
import { buildMeta, breadcrumbSchema, residentialServiceSchemas } from '~/lib/seo';
import { PRICING, SITE  } from '~/data/site';

export const meta: Route.MetaFunction = () => [
  ...buildMeta({
    title: 'Services',
    description:
      'Recurring residential cleaning, one-time deep cleans, and move-in/move-out cleans across Greater Tampa Bay. W-2 technicians, per-sqft pricing.',
    path: '/services',
  }),
  ...residentialServiceSchemas().map((schema) => ({
    'script:ld+json': schema,
  })),
  {
    'script:ld+json': breadcrumbSchema([
      { name: 'Home', url: `${SITE.url}/` },
      { name: 'Services', url: `${SITE.url}/services` },
    ]),
  },
];

export default function ServicesPage() {
  return (
    <main id="main-content">
      {/* ── HEADER ───────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <p className="eyebrow">Residential only · Greater Tampa Bay</p>
          <h1 className="display-lg h1-constrained">
            Three ways we show up.{' '}
            <em className="italic-display">That&rsquo;s it.</em>
          </h1>
          <p className="lede chapter-lede">
            We stopped trying to be everything. No Airbnbs. No offices. No real estate
            surge work. Just the homes we see every week &mdash; which is the only way
            to actually know what a crew can do.
          </p>
        </div>
      </section>

      {/* ── SERVICE 1: RECURRING ─────────────────────────── */}
      <section className="section-sm bg-bone-warm" id="recurring">
        <div className="container">
          <div className="service-block">
            <div className="service-info">
              <span className="chapter-label">Service one</span>
              <h2 className="display-md">Recurring cleaning</h2>
              <p className="lede">
                The core. Weekly, biweekly, or every four weeks. Same crew, same standard,
                same rhythm. Pricing falls as frequency rises &mdash; the more we know your
                home, the less time we need to keep it right.
              </p>
              <div>
                <div className="service-rate-row">
                  <span className="service-rate-label">Weekly</span>
                  <span className="service-rate-price">${PRICING.weeklyRate.toFixed(2)}/sqft</span>
                </div>
                <div className="service-rate-row">
                  <span className="service-rate-label">Biweekly</span>
                  <span className="service-rate-price">${PRICING.biweeklyRate.toFixed(2)}/sqft</span>
                </div>
                <div className="service-rate-row">
                  <span className="service-rate-label">Every four weeks</span>
                  <span className="service-rate-price">${PRICING.monthly4Rate.toFixed(2)}/sqft</span>
                </div>
              </div>
              <p className="marginalia">
                First recurring visit gets a one-time deep-clean upgrade: +${PRICING.deepUpgradeRate.toFixed(2)}/sqft.
              </p>
              <div className="service-cta-row">
                <Link to="/recurring" className="btn btn-brass">How recurring works →</Link>
                <Link to="/quote" className="btn btn-ghost">See your price</Link>
              </div>
            </div>
            <div className="service-visual">
              <ResponsiveImage src="/images/hero-bg.jpg" alt="Cleaned kitchen — New Leaf client home" width={1920} height={1080} priority className="service-visual-photo" />
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICE 2: DEEP CLEAN ────────────────────────── */}
      <section className="section-sm" id="deep">
        <div className="container">
          <div className="service-block service-block-rev">
            <div className="service-info">
              <span className="chapter-label">Service two</span>
              <h2 className="display-md">One-time deep clean</h2>
              <p className="lede">
                For when the house needs a reset. A full top-to-bottom, including inside
                vents, inside windows, behind large furniture, grout, and trim. Built for
                homes that have been sitting, or spring cleanings you&rsquo;ve been
                putting off.
              </p>
              <div>
                <div className="service-rate-row">
                  <span className="service-rate-label">Maintenance deep</span>
                  <span className="service-rate-price">${PRICING.maintenanceRate.toFixed(2)}/sqft</span>
                </div>
                <div className="service-rate-row">
                  <span className="service-rate-label">Full deep clean</span>
                  <span className="service-rate-price">${PRICING.fullDeepRate.toFixed(2)}/sqft</span>
                </div>
              </div>
              <div className="service-cta-row">
                <Link to="/deep" className="btn btn-brass">What a deep clean covers →</Link>
                <Link to="/quote" className="btn btn-ghost">See your price</Link>
              </div>
            </div>
            <div className="service-visual">
              <ResponsiveImage src="https://images.unsplash.com/photo-1769184618473-58c1f0e294f4" alt="Deep clean detail" width={800} height={1000} className="service-visual-photo" />
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICE 3: MOVE-IN/OUT ───────────────────────── */}
      <section className="section-sm bg-bone-warm" id="moveout">
        <div className="container">
          <div className="service-block">
            <div className="service-info">
              <span className="chapter-label">Service three</span>
              <h2 className="display-md">Move-in &amp; move-out</h2>
              <p className="lede">
                Empty house, new chapter. We clean it like we&rsquo;d want to walk into
                ours. Flat rate, walls excluded &mdash; at that point that&rsquo;s the
                painter&rsquo;s job.
              </p>
              <div>
                <div className="service-rate-row">
                  <span className="service-rate-label">Unfurnished flat rate</span>
                  <span className="service-rate-price">${PRICING.moveOutRate.toFixed(2)}/sqft</span>
                </div>
              </div>
              <p className="marginalia">
                Minimum on every service: ${PRICING.minimum}. Even the smallest homes deserve a real visit.
              </p>
              <div className="service-cta-row">
                <Link to="/move-in-out" className="btn btn-brass">What move-out covers →</Link>
                <Link to="/quote" className="btn btn-ghost">See your price</Link>
              </div>
            </div>
            <div className="service-visual">
              <ResponsiveImage src="https://images.unsplash.com/photo-1769745918779-f255f567664c" alt="Empty room move-out clean" width={800} height={1000} className="service-visual-photo" />
            </div>
          </div>
        </div>
      </section>

      {/* ── RESIDENTIAL ONLY ─────────────────────────────── */}
      <section className="dark-section section-sm">
        <div className="dark-section-inner">
          <p className="eyebrow-sage">What we don&rsquo;t do</p>
          <h2 className="display-md">Residential homes only.</h2>
          <p className="lede service-dark-lede">
            We stopped trying to be everything. No Airbnbs. No offices. No real estate
            surge work. Just the homes we see every week &mdash; which is the only way
            to actually know what a crew can do.
          </p>
        </div>
      </section>
      <RelatedPages path="/services" />
    </main>
  );
}
